import React from "react"
import { Grid, Typography } from "@material-ui/core"
import useStyles from "@/popups/components/SelectRounded/styles"

export type SelectOption = { value: string; label: string }

export type MultipleSelectRules = {
	limit?: number
}

type SelectRoundedSingleProps = {
	options: SelectOption[]
	value: string
	onChange: (value: string) => void
	multiple?: false
}

type SelectRoundedMultiProps = {
	options: SelectOption[]
	value: string[]
	onChange: (value: string[]) => void
	multiple: true
	multipleSelectRules?: MultipleSelectRules
}

export type SelectRoundedProps = SelectRoundedSingleProps | SelectRoundedMultiProps

export const SelectRounded: React.FC<SelectRoundedProps> = (props) => {
	const { options } = props
	const classes = useStyles()

	const isOptionSelected = (optionValue: string): boolean => {
		if (props.multiple) return props.value.includes(optionValue)
		return props.value === optionValue
	}

	const handleToggle = (optionValue: string) => {
		if (props.multiple) {
			const isRemoving = props.value.includes(optionValue)
			const isLimitReached = !isRemoving &&
				props.multipleSelectRules?.limit !== undefined &&
				props.value.length >= props.multipleSelectRules.limit

			if (isLimitReached) return

			const next = isRemoving
				? props.value.filter((selectedValue) => selectedValue !== optionValue)
				: [...props.value, optionValue]
			props.onChange(next)
		} else {
			props.onChange(optionValue)
		}
	}

	const isLimitReached = props.multiple &&
		props.multipleSelectRules?.limit !== undefined &&
		props.value.length >= props.multipleSelectRules.limit

	return (
		<Grid container direction="column" spacing={1}>
			{options.map((option) => {
				const isSelected = isOptionSelected(option.value)
				const isDisabled = isLimitReached && !isSelected
				return (
					<Grid item key={option.value}>
						<Grid
							container
							alignItems="center"
							role={props.multiple ? "checkbox" : "radio"}
							aria-checked={isSelected}
							tabIndex={0}
							onClick={() => handleToggle(option.value)}
							onKeyDown={(event: React.KeyboardEvent) => event.key === "Enter" && handleToggle(option.value)}
							className={[classes.row, isSelected ? classes.rowSelected : "", isDisabled ? classes.rowDisabled : ""].filter(Boolean).join(" ")}
						>
							<Grid item>
								<Grid
									container
									alignItems="center"
									justify="center"
									className={[
										classes.bullet,
										props.multiple ? classes.bulletSquare : "",
										isSelected ? classes.bulletSelected : ""
									].filter(Boolean).join(" ")}
								>
									{isSelected && (
										<Grid
											item
											className={[classes.bulletInner, props.multiple ? classes.bulletInnerSquare : ""].filter(Boolean).join(" ")}
										/>
									)}
								</Grid>
							</Grid>
							<Grid item xs>
								<Typography
									className={[classes.label, isSelected ? classes.labelSelected : ""]
										.filter(Boolean)
										.join(" ")}
								>
									{option.label}
								</Typography>
							</Grid>
						</Grid>
					</Grid>
				)
			})}
		</Grid>
	)
}
